import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Lock, KeyRound, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import AdminBreadcrumb from '@/components/AdminBreadcrumb';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminAccountPage() {
  const [current, setCurrent] = useState('');
  const [next1, setNext1] = useState('');
  const [next2, setNext2] = useState('');
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next1 !== next2) {
      toast.error("New passwords don't match");
      return;
    }
    if (next1.length < 8) {
      toast.error('New password must be at least 8 characters');
      return;
    }
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/admin/change-password`, {
        current_password: current,
        new_password: next1,
      });
      if (data?.token) {
        localStorage.setItem('ww-admin-token', data.token);
      }
      setDone(true);
      setCurrent('');
      setNext1('');
      setNext2('');
      toast.success('Password updated. Stay logged in — you\u2019re all set.');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not change password');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="pt-28 pb-16 min-h-screen bg-[#F5F5F0]" data-testid="admin-account-page">
      <div className="container-custom px-6 md:px-12 max-w-2xl mx-auto">
        <AdminBreadcrumb current="Account" />

        <header className="mb-8">
          <p className="text-caption text-[#84A98C] mb-2">Admin</p>
          <h1 className="heading-display text-3xl md:text-4xl text-[#354F52]">Account</h1>
          <p className="text-[#52796F] mt-2 text-sm max-w-xl">
            Rotate the password you use to sign in to <span className="font-mono">/admin</span>.
            The new password is hashed (bcrypt) and stored in the database, so it survives every deploy.
          </p>
        </header>

        <form
          onSubmit={submit}
          className="bg-white rounded-sm border border-[#CAD2C5]/40 p-6 space-y-5"
          data-testid="change-password-form"
        >
          <div className="flex items-center gap-2 mb-2 text-[#354F52]">
            <Lock className="w-5 h-5 text-[#D4AF37]" strokeWidth={1.5} />
            <h2 className="font-serif text-xl">Change password</h2>
          </div>

          <div className="space-y-2">
            <Label htmlFor="current_password" className="text-[#354F52]">Current password</Label>
            <Input
              id="current_password"
              type="password"
              autoComplete="current-password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              required
              data-testid="change-current"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="new_password" className="text-[#354F52]">New password</Label>
            <Input
              id="new_password"
              type="password"
              autoComplete="new-password"
              value={next1}
              onChange={(e) => setNext1(e.target.value)}
              minLength={8}
              required
              data-testid="change-new"
            />
            <p className="text-xs text-[#84A98C]">At least 8 characters. Use something memorable but hard to guess.</p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="new_password_confirm" className="text-[#354F52]">Confirm new password</Label>
            <Input
              id="new_password_confirm"
              type="password"
              autoComplete="new-password"
              value={next2}
              onChange={(e) => setNext2(e.target.value)}
              minLength={8}
              required
              data-testid="change-confirm"
            />
            {next1 && next2 && next1 !== next2 && (
              <p className="text-xs text-[#BC4749]" data-testid="change-mismatch-hint">
                The new passwords don&rsquo;t match yet.
              </p>
            )}
          </div>

          <Button
            type="submit"
            disabled={saving || !current || !next1 || next1 !== next2}
            className="bg-[#354F52] hover:bg-[#52796F] text-[#FAF9F6] w-full"
            data-testid="change-submit"
          >
            {done ? (
              <><Check className="w-4 h-4 mr-2" strokeWidth={2} /> Password updated</>
            ) : (
              <><KeyRound className="w-4 h-4 mr-2" strokeWidth={1.5} /> {saving ? 'Saving…' : 'Update password'}</>
            )}
          </Button>
        </form>
      </div>
    </div>
  );
}
